import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Inject, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { InterceptorConfig } from '@homeleite/arch';
import {
    AnimalSummary,
    MilkControl,
    MilkControlSummary,
    MilkedAnimal,
    Milking,
    Producer,
    ProducerSummary
} from '@homeleite/domain';
import { AnalyticReportService } from '../../../../../../../homeleite/src/app/features/milk-controls/milking-control-details/analytic-report.service';
import { PdfGeneratorService } from '../../../../../../../homeleite/src/app/features/milk-controls/milking-control-details/pdf-generator.service';
import {
    MilkControlEventStatus,
    MilkControlEventsResponse,
    MilkControlsApiRepository
} from '../../../../../../../../libs/domain/src/lib/milk-control/milk-controls-api.repository';
import { catchError, firstValueFrom, map, Observable, of, switchMap, timeout } from 'rxjs';

type PdfKind = 'control' | 'analytic';
type DialogTab = 'control' | 'events' | 'pdfs';

interface PdfState {
    status: 'idle' | 'generating' | 'error';
    error?: string;
}

export const MILK_CONTROL_DIALOG_CONFIG = {
    position: { left: '0', top: '0' },
    width: 'min(1280px, 100vw)',
    maxWidth: '100vw',
    height: '100vh',
    maxHeight: '100vh',
    autoFocus: false,
    panelClass: 'hl-milk-dialog'
};

export const CONTROL_DRAWER_CONFIG = {
    position: { right: '0', top: '0' },
    width: 'min(960px, 100vw)',
    maxWidth: '100vw',
    height: '100vh',
    maxHeight: '100vh',
    autoFocus: false,
    panelClass: 'hl-milk-drawer-right'
};

export interface MilkControlAnimalsDialogData {
    summary: MilkControlSummary;
    producer: Producer;
}

interface AnimalLine {
    nickname: string;
    productions: Record<number, number>;
    reasons: Record<number, string>;
    total: number;
}

interface LotSummary {
    volumes: number[];
    averages: number[];
    grandTotal: number;
    overallAverage: number;
    animalCount: number;
}

interface LotGroup {
    name: string | null;
    animals: AnimalLine[];
    summary: LotSummary;
}

const MISSING_BY_CODE: Record<number, string> = {
    14: 'Cio',
    22: 'Mastite',
    30: 'Recém Parida',
    26: 'Em Tratamento',
    18: 'Exposição',
    68: 'Descarte/Morte'
};

@Component({
    selector: 'homeleite-milk-control-animals-dialog',
    imports: [CommonModule, MatDialogModule, MatIconModule, MatProgressSpinnerModule],
    templateUrl: './producer-details-milk-control-animals-dialog.component.html',
    styleUrls: ['./producer-details-milk-control-animals-dialog.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class ProducerDetailsMilkControlAnimalsDialogComponent implements OnInit {
    isLoading = true;
    hasError = false;
    generatingPdf = false;
    query = '';
    activeTab: DialogTab = 'control';
    readonly pdfItems: { kind: PdfKind; label: string; hint: string }[] = [
        { kind: 'control', label: 'Controle leiteiro', hint: 'Produção de cada animal por ordenha.' },
        { kind: 'analytic', label: 'Analítico', hint: 'Comparação com o controle anterior, ranking, faixas e lotes.' }
    ];
    pdfs: Record<PdfKind, PdfState> = { control: { status: 'idle' }, analytic: { status: 'idle' } };
    control: MilkControl = null;
    orders: number[] = [];
    lots: LotGroup[] = [];
    events: MilkControlEventStatus[] = [];
    eventsReportedAt: string | null = null;
    eventsLoading = false;
    eventsLoaded = false;
    eventsError = false;

    constructor(
        public dialogRef: MatDialogRef<ProducerDetailsMilkControlAnimalsDialogComponent>,
        @Inject(MAT_DIALOG_DATA) public data: MilkControlAnimalsDialogData,
        private repository: MilkControlsApiRepository,
        private pdf: PdfGeneratorService,
        private analytic: AnalyticReportService,
        private changeDetection: ChangeDetectorRef
    ) {}

    ngOnInit(): void {
        // Só o detalhe do controle na abertura — eventos sob demanda na aba (mais rápido).
        loadMilkControlDetail(this.repository, this.data.summary, this.data.producer).subscribe({
            next: (control) => {
                this.control = control;
                this.lots = groupAnimals(control);
                this.orders = milkingOrders(this.lots);
                this.isLoading = false;
                this.changeDetection.markForCheck();
            },
            error: () => {
                this.isLoading = false;
                this.hasError = true;
                this.changeDetection.markForCheck();
            }
        });
    }

    retryEvents(): void {
        this.eventsLoaded = false;
        this.ensureEventsLoaded();
    }

    selectTab(tab: DialogTab): void {
        this.activeTab = tab;
        if (tab === 'events') this.ensureEventsLoaded();
        this.changeDetection.markForCheck();
    }

    get headerAnimalCount(): number | null {
        if (!this.isLoading && !this.hasError) return this.animalCount;
        const fromSummary = (this.data.summary?.milkings || []).reduce(
            (sum, milking) => sum + (Number(milking.totalMilkedAnimals) || 0),
            0
        );
        return fromSummary > 0 ? fromSummary : null;
    }

    private ensureEventsLoaded(): void {
        if (this.eventsLoaded || this.eventsLoading) return;
        this.eventsLoading = true;
        this.eventsError = false;
        this.changeDetection.markForCheck();
        this.repository.listEventsByControl(this.data.summary.id).pipe(timeout(10000)).subscribe({
            next: (response: MilkControlEventsResponse) => {
                this.events = response?.items ?? [];
                this.eventsReportedAt = response?.eventsReportedAt ?? null;
                this.eventsLoading = false;
                this.eventsLoaded = true;
                this.changeDetection.markForCheck();
            },
            error: () => {
                this.eventsLoading = false;
                this.eventsLoaded = true;
                this.eventsError = true;
                this.changeDetection.markForCheck();
            }
        });
    }

    get filteredLots(): LotGroup[] {
        const term = this.query.trim().toLowerCase();
        const lots = !term
            ? this.lots
            : this.lots
                  .map((lot) => ({
                      ...lot,
                      animals: lot.animals.filter((animal) => animal.nickname.toLowerCase().includes(term))
                  }))
                  .filter((lot) => lot.animals.length > 0);
        return lots.map((lot) => ({ ...lot, summary: summarizeLot(lot.animals, this.orders) }));
    }

    get animalCount(): number {
        return this.lots.reduce((sum, lot) => sum + lot.animals.length, 0);
    }

    get controlSummary(): LotSummary {
        return summarizeLot(
            this.lots.flatMap((lot) => lot.animals),
            this.orders
        );
    }

    onSearch(event: Event): void {
        this.query = (event.target as HTMLInputElement).value || '';
    }

    cell(animal: AnimalLine, order: number): string {
        const reason = animal.reasons[order];
        if (reason) return reason;
        const value = animal.productions[order];
        if (value == null || value === 0) return '—';
        return value.toFixed(2).replace('.', ',');
    }

    total(animal: AnimalLine): string {
        return animal.total > 0 ? animal.total.toFixed(2).replace('.', ',') : '—';
    }

    formatLiters(value: number): string {
        return value > 0 ? value.toFixed(2).replace('.', ',') : '—';
    }

    eventTypeLabel(type: string): string {
        const labels: Record<string, string> = {
            CHILDBIRTH: 'Parto', DRY: 'Secagem', HERD_OUT: 'Baixa',
            MATING: 'Cobertura', PREGNANCY: 'Prenhez', MILKED_ANIMAL: 'Produção'
        };
        return labels[type] || type || 'Evento';
    }

    srgStatusLabel(status: string): string {
        const labels: Record<string, string> = {
            PENDING: 'Aguardando envio ao SRG-SCL', SENT: 'Enviado ao SRG-SCL',
            FAILED: 'Falha no SRG-SCL', DISCARDED: 'Descartado no SRG-SCL',
            NOT_REQUIRED: 'Não se aplica', NOT_SENT: 'Ainda não enviado ao SRG-SCL'
        };
        return labels[status] || status;
    }

    srgShortLabel(status: string): string {
        const labels: Record<string, string> = {
            PENDING: 'SRG fila', SENT: 'SRG ok', FAILED: 'SRG falhou',
            DISCARDED: 'SRG descartado', NOT_REQUIRED: 'SRG n/a', NOT_SENT: 'SRG pendente'
        };
        return labels[status] || status || 'SRG';
    }

    isEventBad(event: MilkControlEventStatus): boolean {
        return !event.apiSaved || event.srgStatus === 'FAILED';
    }

    eventErrorText(event: MilkControlEventStatus): string | null {
        const text = (event.apiError || event.srgSclReason || event.lastError || '').trim();
        if (!text) return null;
        const code = event.apiErrorCode || event.srgSclCode;
        return code ? `${text} (${code})` : text;
    }

    async onPdf(): Promise<void> {
        if (!this.control || this.generatingPdf) return;
        this.generatingPdf = true;
        this.changeDetection.markForCheck();
        try {
            await this.pdf.generateMilkControlReport(this.control);
        } finally {
            this.generatingPdf = false;
            this.changeDetection.markForCheck();
        }
    }

    async downloadPdf(kind: PdfKind): Promise<void> {
        if (!this.control || this.pdfs[kind].status === 'generating') return;
        this.pdfs[kind] = { status: 'generating' };
        this.changeDetection.markForCheck();
        try {
            const { fileName, blob } =
                kind === 'analytic'
                    ? await this.analytic.buildAnalyticReport(this.control, await this.loadPrevious())
                    : await this.pdf.buildMilkControlReport(this.control);
            const url = URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = fileName;
            link.click();
            setTimeout(() => URL.revokeObjectURL(url), 60_000);
            this.pdfs[kind] = { status: 'idle' };
        } catch (error) {
            console.error('Falha ao gerar PDF do controle', error);
            this.pdfs[kind] = { status: 'error', error: 'Não foi possível gerar o PDF. Tente de novo.' };
        }
        this.changeDetection.markForCheck();
    }

    /** Controle finalizado imediatamente anterior do mesmo rebanho; null se não houver ou falhar. */
    private async loadPrevious(): Promise<MilkControl | null> {
        try {
            const current = this.data.summary;
            const summaries = await firstValueFrom(
                this.repository.listSummariesByProducer(this.data.producer.id).pipe(timeout(15000))
            );
            const previous = summaries
                .filter((it) => it.id !== current.id && !it.isOpenStatus && new Date(it.startedAt) < new Date(current.startedAt))
                .sort((a, b) => new Date(b.startedAt).getTime() - new Date(a.startedAt).getTime())[0];
            if (!previous) return null;
            return await firstValueFrom(
                loadMilkControlDetail(this.repository, previous, this.data.producer).pipe(timeout(30000))
            );
        } catch (error) {
            console.error('Falha ao carregar o controle anterior', error);
            return null;
        }
    }

    onClose(): void {
        this.dialogRef.close();
    }
}

export function loadMilkControlDetail(
    repository: MilkControlsApiRepository,
    summary: MilkControlSummary,
    producer: Producer
): Observable<MilkControl> {
    return repository.find(summary.id, { autoCatch: InterceptorConfig.NO_INTERCEPT }).pipe(
        switchMap((control) =>
            hasMilkedAnimals(control) ? of(control) : controlFromPackage(repository, summary, producer)
        ),
        catchError(() => controlFromPackage(repository, summary, producer))
    );
}

function hasMilkedAnimals(control: MilkControl): boolean {
    return !!control?.milkings?.some((milking) =>
        milking.milkedAnimals?.some((animal) => animal?.animal?.id || animal?.animal?.nickname)
    );
}

function controlFromPackage(
    repository: MilkControlsApiRepository,
    summary: MilkControlSummary,
    producer: Producer
): Observable<MilkControl> {
    return repository.exportImportJson(summary.id).pipe(
        map((payload) => packageToMilkControl(payload, summary, producer))
    );
}

function packageToMilkControl(payload: any, summary: MilkControlSummary, producer: Producer): MilkControl {
    const rows: any[] = payload?.ttScl || payload?.controls || [];
    const orders = [1, 2, 3].filter((order) =>
        rows.some((row) => volumeOf(row, order) != null || missingOf(row))
    );
    const milkings = (orders.length ? orders : [1]).map((order) => {
        const animals = rows
            .map((row) => toMilkedAnimal(row, order, summary.id))
            .filter((animal): animal is MilkedAnimal => !!animal);
        return new Milking(
            `${summary.id}-${order}`,
            summary.startedAt,
            summary.finishedAt,
            animals,
            summary.status,
            animals.length,
            null,
            order
        );
    }).filter((milking) => milking.milkedAnimals.length > 0);

    return new MilkControl(
        summary.id,
        null,
        summary.startedAt,
        summary.finishedAt,
        summary.status,
        new ProducerSummary(producer?.id, producer?.name, producer?.code),
        null,
        milkings,
        null,
        false
    );
}

function toMilkedAnimal(row: any, order: number, controlId: string): MilkedAnimal | null {
    const production = volumeOf(row, order);
    const reason = missingOf(row);
    if (production == null && !reason) return null;
    const animalId = row.uiIdAnimal || row.animalId || row.apelido || row.animalNickname;
    return new MilkedAnimal(
        `${animalId}-${order}`,
        new AnimalSummary(animalId, '', '', row.registroAnimal || row.animalRegister || '', row.apelido || row.animalNickname || ''),
        null,
        production ?? 0,
        null,
        order,
        1,
        row.loteAnimal || row.animalLot || '',
        reason as MilkedAnimal['missingProduction'],
        null,
        controlId
    );
}

function volumeOf(row: any, order: number): number | null {
    const key =
        order === 1
            ? 'producaoLeitePrimeiraOrdenha'
            : order === 2
              ? 'producaoLeitesegundaOrdenha'
              : 'producaoLeiteTerceiraOrdenha';
    const raw = row[key] ?? row[key.charAt(0).toUpperCase() + key.slice(1)];
    if (raw == null || raw === '') return null;
    const value = Number(String(raw).replace(',', '.'));
    return Number.isFinite(value) ? value : null;
}

function missingOf(row: any): string | null {
    const code = Number(row.ocorrenciaControle ?? row.missingProductionCode);
    return MISSING_BY_CODE[code] || null;
}

function summarizeLot(animals: AnimalLine[], orders: number[]): LotSummary {
    const volumes = orders.map((order) =>
        animals.reduce((sum, animal) => sum + (animal.productions[order] || 0), 0)
    );
    const averages = orders.map((order, index) => {
        const count = animals.filter(
            (animal) => !animal.reasons[order] && (animal.productions[order] || 0) > 0
        ).length;
        return count > 0 ? volumes[index] / count : 0;
    });
    const grandTotal = animals.reduce((sum, animal) => sum + animal.total, 0);
    const withoutMissing = animals.filter((animal) => Object.keys(animal.reasons).length === 0);
    return {
        volumes,
        averages,
        grandTotal,
        overallAverage: withoutMissing.length > 0 ? grandTotal / withoutMissing.length : 0,
        animalCount: animals.length
    };
}

function groupAnimals(control: MilkControl): LotGroup[] {
    const byAnimal = new Map<string, AnimalLine & { lot: string | null }>();
    (control?.milkings || []).forEach((milking, index) => {
        const fallback = milking.milkingNumber || index + 1;
        (milking.milkedAnimals || []).forEach((milked) => {
            const id = milked?.animal?.id || milked?.animal?.nickname;
            if (!id) return;
            const order = milked.milkingNumber || fallback;
            if (!byAnimal.has(id)) {
                byAnimal.set(id, {
                    nickname: milked.animal?.nickname || '—',
                    lot: null,
                    productions: {},
                    reasons: {},
                    total: 0
                });
            }
            const line = byAnimal.get(id);
            if (milked.lot) line.lot = milked.lot;
            const production = Number(milked.production) || 0;
            line.productions[order] = production;
            line.total += production;
            const reason = reasonText(milked.missingProduction);
            if (reason) line.reasons[order] = reason;
        });
    });

    const lots = new Map<string | null, AnimalLine[]>();
    byAnimal.forEach((line) => {
        const key = line.lot && String(line.lot).trim() ? line.lot : null;
        if (!lots.has(key)) lots.set(key, []);
        lots.get(key).push(line);
    });

    return Array.from(lots.entries())
        .map(([name, animals]) => ({
            name,
            animals: animals.sort((a, b) => a.nickname.localeCompare(b.nickname, 'pt-BR', { numeric: true })),
            summary: summarizeLot([], [])
        }))
        .sort((a, b) => {
            if (a.name == null) return 1;
            if (b.name == null) return -1;
            return a.name.localeCompare(b.name, 'pt-BR', { numeric: true });
        });
}

function milkingOrders(lots: LotGroup[]): number[] {
    const orders = new Set<number>();
    lots.forEach((lot) =>
        lot.animals.forEach((animal) => {
            Object.keys(animal.productions).forEach((key) => orders.add(Number(key)));
            Object.keys(animal.reasons).forEach((key) => orders.add(Number(key)));
        })
    );
    return Array.from(orders).filter((order) => order > 0).sort((a, b) => a - b);
}

function reasonText(value: unknown): string | null {
    if (!value) return null;
    if (typeof value === 'string') return value;
    if (typeof value === 'object' && value && 'description' in value) {
        return String((value as { description?: string }).description || '') || null;
    }
    return null;
}
